/* eslint-disable no-console */
import {
  DateRangeFilterConfig,
  FilterConfig,
  FilterGroupConfig,
  InputFilterConfig,
  SearchInputFilterConfig,
} from 'components/SearchAndFilters/FilterTypes';
import {
  MultiSelectFilterConfig,
  MultiSelectRenderStyle,
} from 'components/SearchAndFilters/MultiSelect';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useHydrateAtoms } from 'jotai/utils';
import callApi from 'lib/callApi';
import { fetchHosts } from 'lib/helper';
import React, { useEffect, useState } from 'react';
import { companyIdAtom } from 'views/state';

import { AgeFilterConfig } from './AgeFilter';
import { getAssetsFetchConfig, HostsFilterConfig } from './HostsFilter';
import {
  filterValuesAtom,
  isQuerystringFiltersInitializedAtom,
  parsedFilterValuesAtom,
  policyIdFilterAtom,
  useResetAllPCFindingsAtoms,
} from './state';

export const useFilterConfig = (): FilterConfig => {
  const setFilterValues = useSetAtom(filterValuesAtom);
  const setQuerystringFiltersInitialized = useSetAtom(
    isQuerystringFiltersInitializedAtom,
  );
  const resetAllFilters = useResetAllPCFindingsAtoms();
  const filterValues = useAtomValue(parsedFilterValuesAtom);
  const companyId = useAtomValue(companyIdAtom);
  const [policyId, setPolicyId] = useAtom(policyIdFilterAtom);

  // intermediate state to ensure filters from query string are updated before fetching data
  // hydrate with a default before reading
  useHydrateAtoms([
    [filterValuesAtom, { status: ['Passed', 'Failed'] }],
  ] as const);
  const [queryFiltersUpdated, setQueryFiltersUpdated] = useState(false);
  const [storageItemsHydrated, setStorageItemsHydrated] = useState(false);

  // const technologies = useAtomValue(technologiesDataAtom);
  const [technologyOptions, setTechnologyOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [categoryOptions, setCategoryOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const HOSTS_LIMIT = 100;

  const [hostsData, setHostsData] = useState([]);

  useEffect(() => {
    // ensure the storage items are initialized before reading query string params
    setStorageItemsHydrated(true);
  }, []);

  // initial filter values from query string
  useEffect(() => {
    // exit if storage items are not hydrated yet, or if the query filters are already updated
    if (!storageItemsHydrated || queryFiltersUpdated) return;
    setQuerystringFiltersInitialized(false);

    const queryFilters: Record<string, any> = {};
    const initQueryFilters = async () => {
      const params = new URLSearchParams(window.location.search);
      const entries = Array.from(params.entries());

      for (const [key, value] of entries) {
        if (key === 'policy_id') {
          setPolicyId(value === 'all' ? 'all' : parseInt(value));
        } else if (key === 'host_ids') {
          const policyId = params.get('policy_id');
          const hostIds = value?.split(',')?.map(Number);
          const { baseUrl, additionalParams } = getAssetsFetchConfig(
            // instead of using the state, we can use the value from the query string
            // as state may get updated later
            companyId,
            policyId ? parseInt(policyId) : undefined,
            hostIds,
          );
          const assetsData = await fetchHosts({
            baseUrl: baseUrl,
            search: '',
            page: 0,
            limit: HOSTS_LIMIT,
            additionalParams: additionalParams,
            company_id: companyId,
          });
          if (assetsData) {
            const assets = assetsData?.results || [];
            setHostsData(assets);
            const selectedHosts = assets.filter((asset) =>
              hostIds.includes(asset.host_id),
            );
            queryFilters['host_ids'] = selectedHosts;
          }
        } else if (key.startsWith('dt_filter__')) {
          const filter = key.replace('dt_filter__', '');
          const [filterKey, rangeType] = filter.split('__');
          const fromValue = rangeType === 'from' ? parseInt(value) : null;
          const toValue = rangeType === 'to' ? parseInt(value) : null;
          const filterValue = queryFilters[filterKey]
            ? queryFilters[filterKey]
            : { from: fromValue, to: toValue };
          queryFilters[filterKey] = [filterValue];
        } else if (key.startsWith('age__')) {
          // expected format: age__min=1, age__max=10
          const [filterKey, rangeType] = key.split('__');
          const ageValue = parseInt(value);
          const filterValue = queryFilters[filterKey]
            ? queryFilters[filterKey][0]
            : {
                minValue: undefined,
                maxValue: undefined,
                isMinExclusive: false,
                isMaxExclusive: false,
              };
          if (rangeType === 'min') {
            filterValue.minValue = ageValue;
          } else if (rangeType === 'max') {
            filterValue.maxValue = ageValue;
          } else if (rangeType === 'min_exclusive') {
            filterValue.minValue = ageValue;
            filterValue.isMinExclusive = true;
          } else if (rangeType === 'max_exclusive') {
            filterValue.maxValue = ageValue;
            filterValue.isMaxExclusive = true;
          } else {
            throw new Error(`Invalid age filter range type: ${rangeType}`);
          }

          queryFilters[filterKey] = [filterValue];
        } else if (key === 'closed_by_user') {
          queryFilters['predefined_filters'] = ['closed_by_user=true'];
          filterValues[key] = [value];
        } else {
          queryFilters[key] = value.split(',');
        }
      }
    };

    void initQueryFilters()
      .then(() => {
        if (Object.keys(queryFilters).length > 0) {
          resetAllFilters();
          setFilterValues(queryFilters);
        }

        // update the local state, and wait for it before fetching data in next render cycle
        setQueryFiltersUpdated(true);
      })
      .finally(() => {
        setQuerystringFiltersInitialized(true);
      });

    return () => {
      setQuerystringFiltersInitialized(false);
    };
  }, [queryFiltersUpdated, storageItemsHydrated]);

  useEffect(() => {
    if (queryFiltersUpdated) {
      setQuerystringFiltersInitialized(true);
    }
  }, [queryFiltersUpdated, setQuerystringFiltersInitialized]);

  useEffect(() => {
    const fetchTechnologies = async () => {
      try {
        if (companyId && companyId !== -1) {
          const params = {
            company_id: companyId,
            ...(policyId && policyId !== 'all' && { policy_id: policyId }),
          };

          const responseData = await callApi(
            'pcm/technologies/',
            'GET',
            params,
          );
          const techOptions =
            responseData?.data.map((technology) => ({
              label: technology.technology__name,
              value: technology.technology_id.toString(),
            })) || [];
          setTechnologyOptions(techOptions);
          return;
        }
      } catch (error) {
        console.error(error);
        setTechnologyOptions([]);
      }
    };
    void fetchTechnologies();
  }, [policyId, companyId]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        if (companyId && companyId !== -1) {
          const params = {
            company_id: companyId,
            ...(policyId && policyId !== 'all' && { policy_id: policyId }),
          };

          const responseData = await callApi('pcm/categories/', 'GET', params);
          const catOptions =
            responseData?.data.map((category) => ({
              label: category.category,
              value: category.category,
            })) || [];
          setCategoryOptions(catOptions);
          return;
        }
      } catch (error) {
        console.error(error);
        setCategoryOptions([]);
      }
    };
    void fetchCategories();
  }, [policyId, companyId]);

  const config = React.useMemo(() => {
    const newConfig = new FilterConfig({
      searchFilterId: 'search',
      rightPanelFilters: [
        'status',
        'criticality',
        'technology_id',
        'category',
        'predefined_filters',
        'age',
      ],
      leftPanelFilters: ['host_ids', 'dateRangeFilters', 'control_id'],
      filters: [
        new SearchInputFilterConfig({
          id: 'search',
          label: 'Search',
          placeholder: 'Search and Filter',
          maxLength: 250,
          debounceTime: 1000,
          enableDebounce: true,
          inputProps: { type: 'text' },
          showInFilterMenu: false,
        }),
        new HostsFilterConfig({
          id: 'host_ids',
          label: 'Host Assets',
          badgeLabel: 'Hosts',
          tooltip: (
            <div>
              <p>
                Filter by specific hosts, search is supported. (search by: name,
                alias or IP address)
              </p>
              <p>
                <strong>Note:</strong> The host options will dynamically
                populate based on the selected policy filter. Otherwise, it will
                display hosts for all policies.
              </p>
            </div>
          ),
          iconClass: 'fas fa-cloud',
          hostsData: hostsData,
        }),
        new MultiSelectFilterConfig({
          id: 'status',
          label: 'Status',
          tooltip: (
            <span>
              Filter by status (multi select). <br /> TODO: maybe we should
              explain the error and exception status
            </span>
          ),
          iconClass: 'fas fa-clipboard-check',
          defaultSelectedValues: [''],
          options: [
            { label: 'All', value: '', deselectOthers: true },
            { label: 'Passed', value: 'Passed' },
            { label: 'Failed', value: 'Failed' },
            { label: 'Exception', value: 'Exception' },
            { label: 'Error', value: 'Error' },
          ],
          renderStyle: MultiSelectRenderStyle.Badges,
        }),
        new MultiSelectFilterConfig({
          id: 'technology_id',
          label: 'Technology',
          tooltip: (
            <div>
              <p>Filter by related technologies (multi select).</p>
              <p>
                <strong>Note:</strong> The technology options will dynamically
                populate based on the selected policy filter.
              </p>
            </div>
          ),
          iconClass: 'fas fa-microchip',
          defaultSelectedValues: [''],
          enabled: technologyOptions.length > 0,
          options: [
            { label: 'All', value: '', deselectOthers: true },
            ...technologyOptions,
          ],
          renderStyle:
            technologyOptions.length > 5
              ? MultiSelectRenderStyle.Dropdown
              : MultiSelectRenderStyle.Badges,
        }),
        new MultiSelectFilterConfig({
          id: 'category',
          label: 'Category',
          tooltip: (
            <div>
              <p>Filter by related categories (multi select).</p>
              <p>
                <strong>Note:</strong> The category options will dynamically
                populate based on the selected policy filter.
              </p>
            </div>
          ),
          iconClass: 'fas fa-tags',
          defaultSelectedValues: [''],
          enabled: categoryOptions.length > 0,
          options: [
            { label: 'All', value: '', deselectOthers: true },
            ...categoryOptions,
          ],
          renderStyle:
            categoryOptions.length > 7
              ? MultiSelectRenderStyle.Dropdown
              : MultiSelectRenderStyle.Badges,
        }),
        new MultiSelectFilterConfig({
          id: 'predefined_filters',
          label: 'Predefined Filters',
          tooltip: 'Filter by predefined filters.',
          iconClass: 'fas fa-filter',
          defaultSelectedValues: [''],
          options: [
            { label: 'All', value: '', deselectOthers: true },
            {
              value: 'assigned=true',
              label: 'Assigned',
              deselectOthers: true,
            },
            {
              value: 'self_assigned=true',
              label: 'Assigned To Me',
              deselectOthers: true,
            },
            {
              label: 'Watching (by me)',
              value: 'self_watching=true',
              deselectOthers: true,
            },
            {
              label: 'Unread Notes on Watching Findings',
              value: 'unread_notes=true',
              deselectOthers: true,
            },
            {
              label: 'Closed by user',
              value: 'closed_by_user=true',
              deselectOthers: true,
            },
          ],
          renderStyle: MultiSelectRenderStyle.Badges,
        }),
        new InputFilterConfig({
          id: 'control_id',
          label: 'Control ID',
          tooltip: 'Filter by control ID.',
          iconClass: 'fas fa-id-card',
          inputProps: {
            type: 'number',
            placeholder: 'Control ID',
            maxLength: 50,
          },
          debounceTime: 1000,
          enableDebounce: true,
        }),
        new InputFilterConfig({
          id: 'id',
          label: 'ID',
          tooltip: 'Filter by ID.',
          iconClass: 'fas fa-id-card',
          inputProps: {
            type: 'number',
            placeholder: 'ID',
            maxLength: 50,
          },
          debounceTime: 1000,
          enableDebounce: true,
          showInFilterMenu: false,
        }),
        new MultiSelectFilterConfig({
          id: 'criticality',
          label: 'Severity',
          tooltip: 'Filter by severity (multi select).',
          iconClass: 'fas fa-exclamation-circle',
          defaultSelectedValues: [''],
          options: [
            { label: 'All', value: '', deselectOthers: true },
            { label: 'Urgent', value: '5' },
            { label: 'Critical', value: '4' },
            { label: 'Serious', value: '3' },
            { label: 'Medium', value: '2' },
            { label: 'Minimal', value: '1' },
          ],
          renderStyle: MultiSelectRenderStyle.Badges,
        }),
        new FilterGroupConfig({
          id: 'dateRangeFilters',
          label: 'Filter by Date',
          iconClass: 'fas fa-calendar-alt',
          tooltip:
            'Filter by date and timestamps. The From and To dates are inclusive.',
          filters: [
            new DateRangeFilterConfig({
              id: 'modified_date',
              label: 'Modified Date',
              labelWidth: '115px',
            }),
            new DateRangeFilterConfig({
              id: 'evaluation_date',
              label: 'Evaluation Date',
              labelWidth: '115px',
            }),
            new DateRangeFilterConfig({
              id: 'first_fail_date',
              label: 'First Fail Date',
              labelWidth: '115px',
            }),
            new DateRangeFilterConfig({
              id: 'last_fail_date',
              label: 'Last Fail Date',
              labelWidth: '115px',
            }),
            new DateRangeFilterConfig({
              id: 'first_pass_date',
              label: 'First Pass Date',
              labelWidth: '115px',
            }),
            new DateRangeFilterConfig({
              id: 'last_pass_date',
              label: 'Last Pass Date',
              labelWidth: '115px',
            }),
          ],
        }),
        new AgeFilterConfig({
          id: 'age',
          label: 'Age',
          tooltip: 'Filter by age of the vulnerabilities.',
          iconClass: 'fas fa-clock', // Icon added
          debounceTime: 1000,
          enableDebounce: true,
        }),
      ],
    });

    newConfig.applyInitialFilterValues(filterValues);

    return newConfig;
  }, [filterValues, technologyOptions, categoryOptions]);

  return config;
};
